import { Grid, useTheme } from "@mui/material";
import { Fragment } from "react";

import Mark from "@/Components/Icons/Mark";
import { Divider } from "@/Components/v2/atoms/Divider";
import { Stack } from "@/Components/v2/atoms/Stack";
import { BoldSpan, Typography } from "@/Components/v2/atoms/Typography";
import { HIDE_DECIMAL, NO_SYMBOL } from "@/hooks/useConfig";
import { ExtendedConfigData, FormatCurrencyFunction } from "@/hooks/useConfig/types";
import { ToolTipLegendCircle } from "@/Utils/ECharts/ToolTipLegendCircle";
import { getMappedName } from "@/Utils/mapping-utils";
import { ChannelData } from "@/pages/flighting/types/common";
import { ToolTipContainer } from "@/pages/flighting/components/Chart/Tooltip/TooltipContainer";

interface SpendTooltipProps {
  date: string;
  rollingROI: number | null | undefined;
  channels: ChannelData[];
  totalSpend: number;
  totalRevenue: number;
  config: ExtendedConfigData;
}

const ROIChartTooltipContent = (
  channels: ChannelData[],
  formatCurrency: FormatCurrencyFunction,
  fontSize: string = ""
) => {
  return channels.map((channel) => (
    <Typography key={channel.name} marginY="0.5rem" fontSize={fontSize}>
      <ToolTipLegendCircle color={channel.color} border={channel.color} />
      <BoldSpan>{getMappedName(channel.name) || channel.name}: </BoldSpan>
      {formatCurrency(channel.value, HIDE_DECIMAL)}
    </Typography>
  ));
};

export const BarChartTooltip: React.FC<SpendTooltipProps> = ({
  date,
  rollingROI,
  channels,
  totalSpend,
  totalRevenue,
  config,
}) => {
  const { formatCurrency } = config.currency;
  const { revenueLabel, showReturnInsteadOfRevenue } = config.config;
  const theme = useTheme();
  const revenueFormatterOptions = showReturnInsteadOfRevenue
    ? { ...NO_SYMBOL, ...HIDE_DECIMAL }
    : HIDE_DECIMAL;
  return (
    <ToolTipContainer>
      <Typography fontWeight={600} mb={1}>
        {date}
      </Typography>
      {rollingROI != null && (
        <Fragment>
          <Divider variant="fullWidth" sx={{ paddingY: "0.2rem" }} />
          <Typography variant="body2" marginY={2}>
            <Stack direction="row" alignItems="left" justifyContent="left" spacing={1}>
              <Mark />
              <Typography>
                <BoldSpan>Rolling ROI: </BoldSpan> {rollingROI}%
              </Typography>
            </Stack>
          </Typography>
        </Fragment>
      )}
      {totalRevenue > 0 && (
        <Fragment>
          <Divider variant="fullWidth" sx={{ paddingY: "0.2rem" }} />
          <Typography variant="body2" marginY={2}>
            <Stack direction="row" alignItems="left" justifyContent="left" spacing={1}>
              <Mark markColor={theme.palette.RevenueLineChart.lineColor} />
              <Typography>
                <BoldSpan>{revenueLabel}: </BoldSpan>{" "}
                {formatCurrency(totalRevenue, revenueFormatterOptions)}
              </Typography>
            </Stack>
          </Typography>
        </Fragment>
      )}
      <Divider variant="fullWidth" sx={{ marginY: "1rem" }} />
      {channels.length > 14 ? (
        <Grid container spacing={8}>
          <Grid container item xs={6} direction="column">
            {ROIChartTooltipContent(
              channels.slice(0, Math.ceil(channels.length / 2)),
              formatCurrency,
              "0.875rem"
            )}
          </Grid>
          <Grid container item xs={6} direction="column">
            {ROIChartTooltipContent(
              channels.slice(Math.ceil(channels.length / 2)),
              formatCurrency,
              "0.875rem"
            )}
          </Grid>
        </Grid>
      ) : (
        ROIChartTooltipContent(channels, formatCurrency)
      )}

      <Divider variant="fullWidth" />
      <Typography marginY={1}>
        <BoldSpan>Total Spend: </BoldSpan> {formatCurrency(totalSpend)}
      </Typography>
    </ToolTipContainer>
  );
};

export default BarChartTooltip;
